import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import ProductCard from '../components/ProductCard';
import SEO from '../components/SEO';
import Breadcrumb from '../components/Breadcrumb';
import CategoryHero from '../components/CategoryHero';
import './Shop.css';


// ──────────────────────────────────────────────────────────────────────────
// F6 — Name-based detectors. Applied regardless of original `kind` so
// legacy rows that were already migrated to kind='tag' but kept their
// product-type / rarity / accessory name still land in the right section.
// ──────────────────────────────────────────────────────────────────────────
const RARITY_PREFIX_RE = /^\s*singles\s*-\s*/i;
const isRarityNamed = (name) => RARITY_PREFIX_RE.test(name || '');
const PRODUCT_TYPE_NAME_RE = /^(booster\s*(packs?|boxes?|bundles?)|sealed\s*booster\s*box(es)?|elite\s*trainer\s*box(es)?|etb|collection\s*box(es)?|premium\s*collection|mini\s*tin|tins?|theme\s*deck|battle\s*deck|build\s*&?\s*battle|pin\s*collection|blister\s*pack|box\s*set|mystery\s*box|prerelease\s*kit|graded\s*slabs?|singles)\s*$/i;
const isProductTypeNamed = (name) => PRODUCT_TYPE_NAME_RE.test((name || '').trim());
const ACCESSORY_NAME_RE = /^(accessories?|sleeves|deck\s*boxes?|binders?|pages|playmats?|card\s*storage|top\s*loaders?|penny\s*sleeves?|magnetic\s*one[-\s]?touches?|display\s*cases?|dice|damage\s*counters?|coins|tokens|plushies?|merch)\s*$/i;
const isAccessoryNamed = (name) => ACCESSORY_NAME_RE.test((name || '').trim());

// Normalize category names so plurals / "Sealed " prefixes don't render as
// duplicate filters: "Booster Pack" ≡ "Booster Packs", "Tin" ≡ "Tins",
// "Sealed Booster Boxes" ≡ "Booster Box", etc. Used as a dedupe key across
// the product type + accessory type sections.
const normalizeName = (name) => String(name || '')
  .toLowerCase().trim()
  .replace(/^sealed\s+/, '')                          // drop redundant "Sealed "
  .replace(/\s+/g, ' ')                               // collapse whitespace
  .replace(/(boxes|cases|tins|packs|slabs|sets|collections|decks|kits|bundles|cards)$/,
    (m) => ({                                          // crude plural → singular
      boxes:'box', cases:'case', tins:'tin', packs:'pack', slabs:'slab',
      sets:'set', collections:'collection', decks:'deck', kits:'kit',
      bundles:'bundle', cards:'card',
    })[m] || m);

// ──────────────────────────────────────────────────────────────────────────
// F6 — Tag taxonomy classifier
// ──────────────────────────────────────────────────────────────────────────
// Cross-cutting tags from /api/categories?kind=tag get bucketed by slug.
// Campaign tags (Black Friday, Holiday, Pre-Release, etc.) are NOT shown in
// the sidebar — those belong on product cards as lifecycle badges. Variants
// (Holo, Full Art, …) and editions (1st Edition, Shadowless) become their
// own sections so customers can filter by *card characteristic*.
const VARIANT_TAGS = new Set([
  'holo', 'reverse-holo', 'full-art', 'alt-art',
  'secret-rare', 'rainbow-rare', 'gold-card', 'shiny',
  'jumbo', 'jumbo-cards',
]);
const EDITION_TAGS = new Set([
  '1st-edition', 'shadowless', 'unlimited', 'limited-edition',
]);
const CAMPAIGN_TAGS = new Set([
  'promo', 'promos',                       // dedupe Promo/Promos: shown once under Other
  'black-friday', 'holiday', 'pre-release',
  'restock', 'last-chance', 'coming-soon',
  'clearance', 'new-arrival', 'pre-order',
  'tournament-legal',                      // surfaces as a card badge instead
]);
const OTHER_KEEP = new Set([
  'promo', 'vintage', 'error-misprint', 'signed',
]);

const Shop = () => {
  const params = useParams();
  const [searchParams, setSearchParams] = useSearchParams();

  // Phase 4: nested URL support. Compose the path segments and use the
  // trailing one as the active category for product filtering. The full
  // chain is resolved server-side for breadcrumbs + landing-page hero.
  const pathSlugs = useMemo(() => {
    // /shop/:category (legacy) — params.category
    // /shop/:game/:era/:series/:set (nested) — params.game, .era, .series, .set
    return [params.game, params.era, params.series, params.set, params.category]
      .filter(Boolean);
  }, [params.category, params.game, params.era, params.series, params.set]);
  const category = pathSlugs[pathSlugs.length - 1] || null;
  const [chain, setChain] = useState([]);            // breadcrumb + hero data
  const [redirectingTo, setRedirectingTo] = useState(null); // 301 path lookup

  // Resolve the path chain whenever the URL segments change. Falls back to
  // a legacy slug lookup when there's a single segment (back-compat).
  useEffect(() => {
    if (!pathSlugs.length) { setChain([]); return; }
    fetch(`/api/categories/path?slugs=${encodeURIComponent(pathSlugs.join(','))}`)
      .then(r => r.ok ? r.json() : null)
      .then(j => {
        if (j?.chain) { setChain(j.chain); return; }
        // Single-segment legacy slug — try redirect table
        if (pathSlugs.length === 1) {
          fetch(`/api/categories/redirect/${encodeURIComponent(pathSlugs[0])}`)
            .then(rr => rr.ok ? rr.json() : null)
            .then(rj => { if (rj?.new_path) setRedirectingTo(rj.new_path); });
        }
      })
      .catch(() => {});
  }, [pathSlugs.join('/')]); // eslint-disable-line

  // Phase 4: client 301 — if a redirect was returned, navigate to the new
  // nested URL (preserving query params).
  const navigate = useNavigate();
  useEffect(() => {
    if (redirectingTo) {
      navigate(redirectingTo + (window.location.search || ''), { replace: true });
    }
  }, [redirectingTo, navigate]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  // Default 24 per page (industry-standard density for retail grids). Customer
  // can switch to 48 / 96 via the page-size selector. Persisted in URL.
  const [productsPerPage, setProductsPerPage] = useState(() => {
    const fromUrl = Number(searchParams.get('per_page'));
    return [24, 48, 96].includes(fromUrl) ? fromUrl : 24;
  });

  // F3: All state initialized from URL params so shared/bookmarked links restore
  // exactly what the user saw. Updating any of these writes back to the URL.
  const [search, setSearch]             = useState(() => searchParams.get('q') || '');
  const [sort, setSort]                 = useState(() => searchParams.get('sort') || 'created_at_desc');
  const [view, setView]                 = useState(() => searchParams.get('view') === 'list' ? 'list' : 'grid');
  const [currentPage, setCurrentPage]   = useState(() => Math.max(1, Number(searchParams.get('page')) || 1));
  const [totalCount, setTotalCount]     = useState(0);

  const [filters, setFilters] = useState(() => ({
    minPrice:       searchParams.get('min_price') || '',
    maxPrice:       searchParams.get('max_price') || '',
    rarities:       searchParams.getAll('rarity'),
    conditions:     searchParams.getAll('condition'),
    setName:        searchParams.get('set_name') || '',
    // Default to showing ALL products (in-stock + OOS). Out-of-stock items
    // still render with an "Out of Stock" badge so customers can browse the
    // catalog instead of seeing 0 results when inventory is temporarily
    // depleted. Users can opt back into in-stock-only via the Availability
    // filter section.
    inStockOnly:    searchParams.get('in_stock') === 'true',
    outOfStockOnly: searchParams.get('oos') === '1',
    tags:           searchParams.getAll('tag'),
    psaGrades:      searchParams.getAll('psa'),
  }));

  const [filterOptions, setFilterOptions] = useState({
    rarities: [],
    conditions: [],
    sets: [],             // flat set_name strings from products.set_name column
    tags: [],             // kind='tag' (cross-cutting variants/editions/other)
    games: [],            // kind='game'
    eras: [],             // kind='era'
    series: [],           // kind='series'
    setCats: [],          // kind='set'  (categories with full hierarchy metadata)
    productTypes: [],     // kind='product_type'
    accessoryTypes: [],   // kind='accessory_type'
    priceRange: { min: 0, max: 100 }
  });

  // F2: Live facet counts for the current filter set. Keyed by value:
  // facets.rarities['Holo Rare'] === 12, facets.tags['booster-box'] === 5.
  // Filters with 0 count are hidden in the UI.
  const [facets, setFacets] = useState({ rarities: {}, conditions: {}, sets: {}, tags: {}, psaGrades: {} });

  // Section expand/collapse state. ALL sections start closed by default so
  // the panel is a clean list of headings — customers expand only what they
  // want to filter by. Mirrors how Amazon / Wayfair / 401 Games handle long
  // filter lists.
  const [expanded, setExpanded] = useState({
    game:        false,
    era:         false,
    series:      false,
    setCat:      false,
    productType: false,
    condition:   false,
    variant:     false,
    edition:     false,
    accessory:   false,
    price:       false,
    availability: false,
    other:       false,
  });
  const toggleSection = (k) => setExpanded(p => ({ ...p, [k]: !p[k] }));

  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => { fetchProducts(); /* eslint-disable-next-line */ }, [category, search, sort, currentPage, productsPerPage, filters]);
  useEffect(() => { fetchFilterOptions(); fetchTagBuckets(); }, []);

  // F3: Mirror state into the URL so links are shareable / bookmarkable and
  // the back button restores the last filter set. Replace (don't push) so we
  // don't blow up history with every keystroke.
  useEffect(() => {
    const next = new URLSearchParams();
    if (search) next.set('q', search);
    if (sort && sort !== 'created_at_desc') next.set('sort', sort);
    if (view === 'list') next.set('view', 'list');
    if (productsPerPage !== 24) next.set('per_page', String(productsPerPage));
    if (currentPage > 1) next.set('page', String(currentPage));
    if (filters.minPrice) next.set('min_price', filters.minPrice);
    if (filters.maxPrice) next.set('max_price', filters.maxPrice);
    filters.rarities.forEach(r => next.append('rarity', r));
    filters.conditions.forEach(c => next.append('condition', c));
    filters.tags.forEach(t => next.append('tag', t));
    filters.psaGrades.forEach(g => next.append('psa', g));
    if (filters.setName) next.set('set_name', filters.setName);
    if (filters.outOfStockOnly) next.set('oos', '1');
    if (filters.inStockOnly) next.set('in_stock', 'true');
    setSearchParams(next, { replace: true });
  }, [search, sort, view, currentPage, productsPerPage, filters, setSearchParams]);

  const fetchFilterOptions = async () => {
    try {
      const response = await fetch('/api/products/filters/options');
      if (!response.ok) return;
      const data = await response.json();
      setFilterOptions(o => ({
        ...o,
        rarities:   Array.isArray(data?.rarities)   ? data.rarities   : o.rarities,
        conditions: Array.isArray(data?.conditions) ? data.conditions : o.conditions,
        sets:       Array.isArray(data?.sets)       ? data.sets       : o.sets,
        priceRange: data?.priceRange || o.priceRange,
      }));
    } catch (err) { console.error('Error fetching filter options:', err); }
  };

  // Fetch all tag-shaped categories: kind=tag (variants, editions, other),
  // kind=product_type (Sealed buckets), kind=accessory_type. One bulk fetch
  // and split client-side.
  const fetchTagBuckets = async () => {
    try {
      const r = await fetch('/api/categories?nocache=1');
      if (!r.ok) return;
      const rows = await r.json();
      if (!Array.isArray(rows)) return;
      // Sort by sort_order (release order — newest first), then name as tiebreaker.
      const bySortOrder = (a, b) => (a.sort_order || 0) - (b.sort_order || 0) || (a.name || '').localeCompare(b.name || '');

      // Mis-classified row detectors. These rows live as kind='set' but are
      // actually rarity buckets, product types, or umbrella categories — they
      // should not pollute the Set filter list. Migration moves them to the
      // right kind permanently; until then we hide them client-side.
      const isRarityMisclassifiedSet  = (c) => /^\s*singles\s*-/i.test(c.name || '');
      const isProductTypeMisclassifiedSet = (c) => /^(booster\s*(packs?|boxes?|bundles?)|elite\s*trainer\s*box(es)?|etb|collection\s*box(es)?|premium\s*collection|mini\s*tin|tins?|theme\s*deck|battle\s*deck|build\s*&?\s*battle|pin\s*collection|blister\s*pack|box\s*set|mystery\s*box|prerelease\s*kit)\s*$/i
        .test((c.name || '').trim());
      const isGenericUmbrella = (c) => /^(pok[eé]mon\s*cards?|all\s*products|cards?)$/i.test((c.name || '').trim());
      const shouldHideFromSets = (c) =>
        isRarityMisclassifiedSet(c) || isProductTypeMisclassifiedSet(c) || isGenericUmbrella(c);

      const setCats = rows
        .filter(c => c.kind === 'set' && !shouldHideFromSets(c))
        .sort(bySortOrder);
      // Rescue rows: legacy "Singles - X" categories surface as variant tags
      // with the "Singles - " prefix stripped so they read as plain rarity
      // names (Common, Holo Rare, Ultra Rare, etc.). Marked `_isRarity` so
      // the render layer puts them in the Rarity & variant section.
      const rescuedRarities = rows
        .filter(c => c.kind === 'set' && isRarityMisclassifiedSet(c))
        .map(c => ({
          ...c,
          name: (c.name || '').replace(/^\s*singles\s*-\s*/i, '').trim(),
          _rescuedFrom: 'set',
          _isRarity: true,
        }));
      const rescuedProductTypes = rows
        .filter(c => c.kind === 'set' && isProductTypeMisclassifiedSet(c))
        .map(c => ({ ...c, _rescuedFrom: 'set' }));
      // Dedupe product types so a rescued "Booster Packs" doesn't double up
      // with the canonical "Booster Box" / "Booster Pack" rows from the seed.
      const canonicalPTSlugs = new Set(
        rows.filter(c => c.kind === 'product_type').map(c => c.slug)
      );
      const rescuedPTUnique = rescuedProductTypes.filter(c => !canonicalPTSlugs.has(c.slug));

      setFilterOptions(o => ({
        ...o,
        tags:           [...rows.filter(c => c.kind === 'tag'), ...rescuedRarities].sort(bySortOrder),
        productTypes:   [...rows.filter(c => c.kind === 'product_type'), ...rescuedPTUnique].sort(bySortOrder),
        accessoryTypes: rows.filter(c => c.kind === 'accessory_type').sort(bySortOrder),
        games:          rows.filter(c => c.kind === 'game').sort(bySortOrder),
        eras:           rows.filter(c => c.kind === 'era').sort(bySortOrder),
        series:         rows.filter(c => c.kind === 'series').sort(bySortOrder),
        setCats,
      }));
    } catch (_) {}
  };

  const fetchProducts = () => {
    setLoading(true);
    const params = new URLSearchParams({
      limit: productsPerPage,
      offset: (currentPage - 1) * productsPerPage,
      sort
    });
    if (category) params.append('category', category);
    if (search) params.append('search', search);
    if (filters.minPrice) params.append('min_price', filters.minPrice);
    if (filters.maxPrice) params.append('max_price', filters.maxPrice);
    filters.rarities.forEach(r => params.append('rarity', r));
    filters.conditions.forEach(c => params.append('condition', c));
    if (filters.setName) params.append('set_name', filters.setName);
    if (filters.outOfStockOnly) params.append('out_of_stock_only', '1');
    else if (filters.inStockOnly !== false) params.append('in_stock', 'true');
    (filters.tags || []).forEach(t => params.append('tag', t));
    (filters.psaGrades || []).forEach(g => params.append('psa_grade', g));

    Promise.all([
      fetch(`/api/products?${params}`).then(res => res.ok ? res.json() : []).catch(() => []),
      fetch(`/api/products/count/all?${params}`).then(res => res.ok ? res.json() : { count: 0 }).catch(() => ({ count: 0 })),
      // F2 — facet counts run in parallel with the main fetch.
      fetch(`/api/products/filters/facets?${params}`).then(res => res.ok ? res.json() : null).catch(() => null),
    ])
      .then(([productsData, countData, facetData]) => {
        setProducts(Array.isArray(productsData) ? productsData : (productsData?.products || []));
        setTotalCount(countData?.count || 0);
        if (facetData) {
          setFacets({
            rarities:   facetData.rarities   || {},
            conditions: facetData.conditions || {},
            sets:       facetData.sets       || {},
            tags:       facetData.tags       || {},
            psaGrades:  facetData.psaGrades  || {},
          });
        }
        setLoading(false);
      })
      .catch(err => { console.error('Error fetching products:', err); setLoading(false); });
  };

  // ── Filter manipulation ──────────────────────────────────────────────
  const handleSearchChange = (e) => { setSearch(e.target.value); setCurrentPage(1); };
  const handleSortChange = (e)   => { setSort(e.target.value); setCurrentPage(1); };
  const handleFilterChange = (k, v) => { setFilters(p => ({ ...p, [k]: v })); setCurrentPage(1); };
  const toggleMulti = (k, v) => {
    setFilters(p => {
      const cur = p[k] || [];
      return { ...p, [k]: cur.includes(v) ? cur.filter(x => x !== v) : [...cur, v] };
    });
    setCurrentPage(1);
  };

  const clearAllFilters = () => {
    setFilters({
      minPrice: '', maxPrice: '', rarities: [], conditions: [], psaGrades: [],
      setName: '', inStockOnly: true, outOfStockOnly: false, tags: [],
    });
    setCurrentPage(1);
  };

  // ── Derived: cascading set/series filter based on selected Era ───────
  // When a user ticks "Scarlet & Violet" under Era, the Set filter list
  // narrows to only sets whose ancestor chain includes Scarlet & Violet.
  // Same logic cascades Game → Era → Series → Set.
  const cascadedSetCats = useMemo(() => {
    const all = [
      ...filterOptions.games,
      ...filterOptions.eras,
      ...filterOptions.series,
      ...filterOptions.setCats,
    ];
    const byId = new Map(all.map(c => [c.id, c]));

    // Which ancestor slugs has the user selected (from filters.tags)?
    const selectedAncestorIds = new Set(
      [...filterOptions.games, ...filterOptions.eras, ...filterOptions.series]
        .filter(c => filters.tags.includes(c.slug))
        .map(c => c.id)
    );
    if (selectedAncestorIds.size === 0) return filterOptions.setCats;

    const ancestorIdsOf = (c) => {
      const ids = [];
      let cur = c.parent_id;
      const seen = new Set();
      while (cur != null && byId.has(cur) && !seen.has(cur)) {
        seen.add(cur); ids.push(cur); cur = byId.get(cur)?.parent_id;
      }
      return ids;
    };
    return filterOptions.setCats.filter(s => {
      const anc = ancestorIdsOf(s);
      // Show this set if at least one selected ancestor is on its chain.
      return anc.some(id => selectedAncestorIds.has(id));
    });
  }, [filterOptions.games, filterOptions.eras, filterOptions.series, filterOptions.setCats, filters.tags]);

  // Same cascade for Series — only show series whose ancestor chain contains
  // a selected Game or Era.
  const cascadedSeries = useMemo(() => {
    const allLevels = [...filterOptions.games, ...filterOptions.eras, ...filterOptions.series];
    const byId = new Map(allLevels.map(c => [c.id, c]));
    const selectedAncestorIds = new Set(
      [...filterOptions.games, ...filterOptions.eras]
        .filter(c => filters.tags.includes(c.slug))
        .map(c => c.id)
    );
    if (selectedAncestorIds.size === 0) return filterOptions.series;
    const ancestorIdsOf = (c) => {
      const ids = [];
      let cur = c.parent_id;
      const seen = new Set();
      while (cur != null && byId.has(cur) && !seen.has(cur)) {
        seen.add(cur); ids.push(cur); cur = byId.get(cur)?.parent_id;
      }
      return ids;
    };
    return filterOptions.series.filter(s => ancestorIdsOf(s).some(id => selectedAncestorIds.has(id)));
  }, [filterOptions.games, filterOptions.eras, filterOptions.series, filters.tags]);

  // ── Derived: classify tags into rarity / variant / edition / product /
  // accessory / other buckets. Each row is checked first by slug (canonical
  // tags), then by NAME (legacy rows that were renamed/kind-changed but kept
  // a misclassified-shaped name like "Singles - Holo Rare").
  const tagBuckets = useMemo(() => {
    const tags = filterOptions.tags || [];
    const rarities = [];
    const variants = [];
    const editions = [];
    const productLike = [];
    const accessoryLike = [];
    const others = [];
    for (const t of tags) {
      const slug = (t.slug || '').toLowerCase();
      const name = t.name || '';
      if (CAMPAIGN_TAGS.has(slug) && !OTHER_KEEP.has(slug)) continue;

      // Rarity detection (slug OR name shape)
      if (t._isRarity || isRarityNamed(name)) {
        rarities.push({ ...t, name: name.replace(RARITY_PREFIX_RE, '').trim() });
        continue;
      }

      // Product-type-shaped names → route to productLike (rendered in Product type section)
      if (isProductTypeNamed(name)) { productLike.push(t); continue; }
      // Accessory-shaped names → accessoryLike
      if (isAccessoryNamed(name))    { accessoryLike.push(t); continue; }

      if (VARIANT_TAGS.has(slug)) variants.push(t);
      else if (EDITION_TAGS.has(slug)) editions.push(t);
      else others.push(t);
    }
    return { rarities, variants, editions, productLike, accessoryLike, others };
  }, [filterOptions.tags]);

  // ── Derived: list of currently-active filters for the chip bar ───────
  const activeChips = useMemo(() => {
    const chips = [];
    if (category) chips.push({ key: 'category', label: category.replace(/-/g, ' '), onClear: null }); // category is URL-driven, no client clear
    if (filters.minPrice || filters.maxPrice) {
      chips.push({
        key: 'price',
        label: `$${filters.minPrice || '0'} – $${filters.maxPrice || '∞'}`,
        onClear: () => setFilters(p => ({ ...p, minPrice: '', maxPrice: '' })),
      });
    }
    for (const r of filters.rarities)   chips.push({ key: `r-${r}`, label: r, onClear: () => toggleMulti('rarities', r) });
    for (const c of filters.conditions) chips.push({ key: `c-${c}`, label: c, onClear: () => toggleMulti('conditions', c) });
    for (const g of filters.psaGrades)  chips.push({ key: `g-${g}`, label: g === 'graded' ? 'Any graded' : `PSA ${g}`, onClear: () => toggleMulti('psaGrades', g) });
    if (filters.setName) chips.push({ key: 'set', label: filters.setName, onClear: () => handleFilterChange('setName', '') });
    for (const t of filters.tags) {
      const meta = filterOptions.tags.find(x => x.slug === t)
                 || filterOptions.productTypes.find(x => x.slug === t)
                 || filterOptions.accessoryTypes.find(x => x.slug === t);
      chips.push({ key: `t-${t}`, label: meta?.name || t, onClear: () => toggleMulti('tags', t) });
    }
    if (filters.outOfStockOnly) chips.push({ key: 'oos', label: 'Out of stock only', onClear: () => { handleFilterChange('outOfStockOnly', false); handleFilterChange('inStockOnly', true); } });
    return chips;
  }, [filters, category, filterOptions]);

  const hasActiveFilters = activeChips.length > 0;
  const totalPages = Math.ceil(totalCount / productsPerPage);
  const categoryLabel = category ? category.replace(/-/g, ' ') : 'All Products';

  // ── Reusable filter section ─────────────────────────────────────────
  const Section = ({ id, title, count, children }) => (
    <div className={`filter-section${expanded[id] ? ' is-open' : ''}`}>
      <button
        type="button"
        className="filter-section__header"
        onClick={() => toggleSection(id)}
        aria-expanded={!!expanded[id]}
      >
        <span>{title}</span>
        {count != null && <span className="filter-section__count">{count}</span>}
        <svg className="filter-section__chevron" width="14" height="14" viewBox="0 0 24 24"
             fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
             aria-hidden="true">
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>
      {expanded[id] && <div className="filter-section__body">{children}</div>}
    </div>
  );

  // Renders a tag/product-type/accessory checkbox. Hidden when the live
  // facet count is 0 unless the tag is currently selected (so the user can
  // still un-tick it).
  const tagCheckbox = (t) => {
    const checked = filters.tags.includes(t.slug);
    const count = facets.tags[t.slug] ?? null;
    if (!checked && count === 0) return null;
    return (
      <label key={t.id || t.slug} className="checkbox-label">
        <input
          type="checkbox"
          checked={checked}
          onChange={() => toggleMulti('tags', t.slug)}
        />
        <span>{t.name}{count != null && <small className="filter-count"> ({count})</small>}</span>
      </label>
    );
  };

  // Helper: hide options with 0 facet count (unless selected).
  const visibleWithCount = (values, facetMap, isSelected) =>
    values.filter(v => isSelected(v) || (facetMap[v] ?? null) !== 0);

  return (
    <div className="shop">
      <SEO
        title={category ? `${categoryLabel} — Pokemon TCG` : 'Shop all Pokemon TCG products'}
        description={category
          ? `Browse ${categoryLabel} from SilverGames. Daily restocks, tracked Canadian shipping, +20% buylist bonus.`
          : 'Browse our full Pokemon TCG inventory — singles, sealed product, graded slabs. Tracked shipping across Canada.'}
      />
      {/* Phase 4: breadcrumb + landing hero when on a nested category page */}
      {chain.length > 0 && <Breadcrumb chain={chain} />}
      {chain.length > 0 && (
        <CategoryHero
          leaf={chain[chain.length - 1]}
          basePath={'/shop/' + chain.map(c => c.slug).join('/')}
        />
      )}

      {chain.length === 0 && (
        <div className="shop-header">
          <h1>{category ? categoryLabel.toUpperCase() : 'All Products'}</h1>
          <p className="product-count">{totalCount} products found</p>
        </div>
      )}
      {chain.length > 0 && (
        <p className="product-count">{totalCount.toLocaleString()} products in {chain[chain.length - 1].name}</p>
      )}

      <div className="shop-controls">
        <div className="search-box">
          <input
            type="text"
            placeholder="Search products..."
            value={search}
            onChange={handleSearchChange}
            aria-label="Search products"
          />
        </div>

        <button
          className="btn btn-secondary filter-toggle"
          onClick={() => setShowFilters(!showFilters)}
          aria-expanded={showFilters}
        >
          {showFilters ? 'Hide Filters' : 'Show Filters'}
          {hasActiveFilters && <span className="filter-badge">{activeChips.length}</span>}
        </button>

        <div className="sort-box">
          <label htmlFor="per-page-select">Show:</label>
          <select
            id="per-page-select"
            value={productsPerPage}
            onChange={(e) => { setProductsPerPage(Number(e.target.value)); setCurrentPage(1); }}
            aria-label="Products per page"
          >
            <option value={24}>24</option>
            <option value={48}>48</option>
            <option value={96}>96</option>
          </select>
        </div>

        <div className="sort-box">
          <label htmlFor="sort-select">Sort by:</label>
          <select id="sort-select" value={sort} onChange={handleSortChange}>
            <option value="created_at_desc">Newest first</option>
            <option value="value">Best value (below market)</option>
            <option value="created_at_asc">Oldest first</option>
            <option value="price_asc">Price: low to high</option>
            <option value="price_desc">Price: high to low</option>
            <option value="name_asc">Name: A → Z</option>
            <option value="name_desc">Name: Z → A</option>
            <option value="stock_desc">In stock</option>
            <option value="rarity_asc">Rarity</option>
          </select>
        </div>

        {/* F3: Grid / List view toggle. Persisted to URL. */}
        <div className="view-toggle" role="group" aria-label="View">
          <button
            type="button"
            className={`view-toggle__btn${view === 'grid' ? ' is-active' : ''}`}
            onClick={() => setView('grid')}
            aria-pressed={view === 'grid'}
            aria-label="Grid view"
            title="Grid view"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <rect x="3" y="3" width="7" height="7"/>
              <rect x="14" y="3" width="7" height="7"/>
              <rect x="3" y="14" width="7" height="7"/>
              <rect x="14" y="14" width="7" height="7"/>
            </svg>
          </button>
          <button
            type="button"
            className={`view-toggle__btn${view === 'list' ? ' is-active' : ''}`}
            onClick={() => setView('list')}
            aria-pressed={view === 'list'}
            aria-label="List view"
            title="List view"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <line x1="8" y1="6" x2="21" y2="6"/>
              <line x1="8" y1="12" x2="21" y2="12"/>
              <line x1="8" y1="18" x2="21" y2="18"/>
              <circle cx="4" cy="6" r="1"/>
              <circle cx="4" cy="12" r="1"/>
              <circle cx="4" cy="18" r="1"/>
            </svg>
          </button>
        </div>
      </div>

      {/* Active filter chip bar — visible whenever ANY filter is on */}
      {hasActiveFilters && (
        <div className="filter-chips" role="region" aria-label="Active filters">
          <span className="filter-chips__label">Filtering by:</span>
          {activeChips.map(chip => (
            chip.onClear ? (
              <button key={chip.key} type="button" className="filter-chip" onClick={chip.onClear}>
                {chip.label}
                <span className="filter-chip__x" aria-hidden="true">×</span>
              </button>
            ) : (
              <span key={chip.key} className="filter-chip filter-chip--locked">{chip.label}</span>
            )
          ))}
          <button type="button" className="filter-chips__clear" onClick={clearAllFilters}>Clear all</button>
        </div>
      )}

      {/* F4: Mobile drawer — when showFilters is true on mobile, the filter
          panel becomes a full-height slide-in over the page. Same accordion
          markup, different positioning via .filter-panel.is-mobile-drawer.
          Backdrop closes the drawer; the bottom "Apply" CTA stays sticky so
          the user always has an obvious dismiss. */}
      {showFilters && (
        <div className="filter-drawer-backdrop" onClick={() => setShowFilters(false)} aria-hidden="true" />
      )}

      {showFilters && (
        <div className="filter-panel is-mobile-drawer" role="dialog" aria-modal="true" aria-label="Product filters">
          <div className="filter-header">
            <h3>Filters{hasActiveFilters && <span className="filter-header__count"> · {activeChips.length}</span>}</h3>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
              {hasActiveFilters && (
                <button type="button" className="btn btn-link clear-filters" onClick={clearAllFilters}>
                  Clear all
                </button>
              )}
              <button
                type="button"
                className="filter-drawer-close"
                onClick={() => setShowFilters(false)}
                aria-label="Close filters"
              >
                ×
              </button>
            </div>
          </div>

          <div className="filter-sections">
            {/* 0a. Game */}
            {filterOptions.games.length > 0 && (
              <Section id="game" title="Game" count={filterOptions.games.length}>
                <div className="checkbox-group">
                  {filterOptions.games.map(tagCheckbox)}
                </div>
              </Section>
            )}

            {/* 0b. Era */}
            {filterOptions.eras.length > 0 && (
              <Section id="era" title="Era" count={filterOptions.eras.length}>
                <div className="checkbox-group">
                  {filterOptions.eras.map(tagCheckbox)}
                </div>
              </Section>
            )}

            {/* 0c. Series — narrowed by selected Game/Era */}
            {cascadedSeries.length > 0 && (
              <Section id="series" title="Series" count={cascadedSeries.length}>
                <div className="checkbox-group">
                  {cascadedSeries.map(tagCheckbox)}
                </div>
              </Section>
            )}

            {/* 0d. Set — cascades from the active Game/Era/Series so the list
                stays scannable instead of dumping all 22+ sets at once. */}
            {cascadedSetCats.length > 0 && (
              <Section id="setCat" title="Set" count={cascadedSetCats.length}>
                <div className="checkbox-group">
                  {cascadedSetCats.map(tagCheckbox)}
                </div>
              </Section>
            )}

            {/* 1. Set / Expansion — only show sets with > 0 matching products */}
            {(() => {
              const visibleSets = visibleWithCount(filterOptions.sets, facets.sets, s => s === filters.setName);
              if (!visibleSets.length) return null;
              return (
                <Section id="set" title="Set / Expansion" count={visibleSets.length}>
                  <select
                    value={filters.setName}
                    onChange={(e) => handleFilterChange('setName', e.target.value)}
                    className="filter-select"
                    aria-label="Filter by set or expansion"
                  >
                    <option value="">All sets</option>
                    {visibleSets.map(set => (
                      <option key={set} value={set}>
                        {set}{facets.sets[set] != null ? ` (${facets.sets[set]})` : ''}
                      </option>
                    ))}
                  </select>
                </Section>
              );
            })()}

            {/* 2. Product Type — kind='product_type' rows + tag-kind rows
                whose NAME matches a product-type shape (Booster Packs, ETB,
                Collection Boxes, etc.). Deduped by slug so canonical seeds
                don't double up with legacy rescued rows. */}
            {(() => {
              // Dedupe by normalized name so "Booster Pack" + "Booster Packs"
              // + "Sealed Booster Boxes" + "Booster Box" all collapse to one
              // entry. Canonical seed rows (kind='product_type') win because
              // they're iterated first.
              const seen = new Set();
              const merged = [];
              for (const t of [...filterOptions.productTypes, ...tagBuckets.productLike]) {
                const key = normalizeName(t.name);
                if (seen.has(key)) continue;
                seen.add(key);
                merged.push(t);
              }
              if (!merged.length) return null;
              return (
                <Section id="productType" title="Product type" count={merged.length}>
                  <div className="checkbox-group">{merged.map(tagCheckbox)}</div>
                </Section>
              );
            })()}

            {/* 3. Condition & Grade — Condition + PSA combined */}
            {(filterOptions.conditions.length > 0 || true) && (
              <Section id="condition" title="Condition & grade">
                {(() => {
                  const isSel = v => filters.conditions.includes(v);
                  const visible = visibleWithCount(filterOptions.conditions, facets.conditions, isSel);
                  if (!visible.length) return null;
                  return (
                    <div className="checkbox-group">
                      {visible.map(condition => {
                        const count = facets.conditions[condition];
                        return (
                          <label key={condition} className="checkbox-label">
                            <input
                              type="checkbox"
                              checked={isSel(condition)}
                              onChange={() => toggleMulti('conditions', condition)}
                            />
                            <span>{condition}{count != null && <small className="filter-count"> ({count})</small>}</span>
                          </label>
                        );
                      })}
                    </div>
                  );
                })()}
                {Object.keys(facets.conditions).length > 0 && Object.keys(facets.psaGrades).length > 0 && <div className="filter-divider" />}
                {(() => {
                  const grades = ['10', '9', '8', '7'];
                  const visibleGrades = grades.filter(g => filters.psaGrades.includes(g) || (facets.psaGrades[g] ?? 0) > 0);
                  const anyGradedCount = Object.values(facets.psaGrades).reduce((a, b) => a + b, 0);
                  const showAnyGraded = filters.psaGrades.includes('graded') || anyGradedCount > 0;
                  if (!visibleGrades.length && !showAnyGraded) return null;
                  return (
                    <div className="checkbox-group">
                      {visibleGrades.map(grade => (
                        <label key={grade} className="checkbox-label">
                          <input
                            type="checkbox"
                            checked={filters.psaGrades.includes(grade)}
                            onChange={() => toggleMulti('psaGrades', grade)}
                          />
                          <span>PSA {grade}{facets.psaGrades[grade] != null && <small className="filter-count"> ({facets.psaGrades[grade]})</small>}</span>
                        </label>
                      ))}
                      {showAnyGraded && (
                        <label className="checkbox-label">
                          <input
                            type="checkbox"
                            checked={filters.psaGrades.includes('graded')}
                            onChange={() => toggleMulti('psaGrades', 'graded')}
                          />
                          <span>Any graded{anyGradedCount > 0 && <small className="filter-count"> ({anyGradedCount})</small>}</span>
                        </label>
                      )}
                    </div>
                  );
                })()}
              </Section>
            )}

            {/* 4. Rarity & Variant — merged source: products.rarity column +
                rescued legacy "Singles - X" tag rows (deduped by name) +
                variant tags (Holo, Full Art, etc.). One single rarity list. */}
            {(() => {
              // Merge column-derived rarities with rescued tag-derived ones.
              // Dedupe by lowercased name so "Holo Rare" from both sources
              // doesn't appear twice.
              const seen = new Set();
              const merged = [];
              for (const r of filterOptions.rarities) {
                const k = String(r).toLowerCase();
                if (seen.has(k)) continue;
                seen.add(k);
                merged.push({ source: 'column', value: r, label: r });
              }
              for (const t of tagBuckets.rarities) {
                const k = String(t.name).toLowerCase();
                if (seen.has(k)) continue;
                seen.add(k);
                merged.push({ source: 'tag', value: t.slug, label: t.name, raw: t });
              }
              if (!merged.length && tagBuckets.variants.length === 0) return null;
              return (
                <Section id="variant" title="Rarity & variant" count={merged.length + tagBuckets.variants.length}>
                  {merged.length > 0 && (
                    <div className="checkbox-group">
                      {merged.map(m => {
                        const isCol = m.source === 'column';
                        const checked = isCol ? filters.rarities.includes(m.value) : filters.tags.includes(m.value);
                        const count = isCol ? facets.rarities[m.value] : facets.tags[m.value];
                        if (!checked && count === 0) return null; // F2 hide-zero
                        return (
                          <label key={`${m.source}-${m.value}`} className="checkbox-label">
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => isCol ? toggleMulti('rarities', m.value) : toggleMulti('tags', m.value)}
                            />
                            <span>{m.label}{count != null && <small className="filter-count"> ({count})</small>}</span>
                          </label>
                        );
                      })}
                    </div>
                  )}
                  {merged.length > 0 && tagBuckets.variants.length > 0 && <div className="filter-divider" />}
                  {tagBuckets.variants.length > 0 && (
                    <div className="checkbox-group">
                      {tagBuckets.variants.map(tagCheckbox)}
                    </div>
                  )}
                </Section>
              );
            })()}

            {/* 5. Edition — 1st Edition, Shadowless, etc. */}
            {tagBuckets.editions.length > 0 && (
              <Section id="edition" title="Edition" count={tagBuckets.editions.length}>
                <div className="checkbox-group">
                  {tagBuckets.editions.map(tagCheckbox)}
                </div>
              </Section>
            )}

            {/* 6. Accessory type — canonical kind='accessory_type' + legacy
                tag-kind rows whose name matches an accessory shape. */}
            {(() => {
              // Dedupe by normalized name (plurals collapse, etc.).
              const seen = new Set();
              const merged = [];
              for (const t of [...filterOptions.accessoryTypes, ...tagBuckets.accessoryLike]) {
                const key = normalizeName(t.name);
                if (seen.has(key)) continue;
                seen.add(key);
                merged.push(t);
              }
              if (!merged.length) return null;
              return (
                <Section id="accessory" title="Accessory type" count={merged.length}>
                  <div className="checkbox-group">{merged.map(tagCheckbox)}</div>
                </Section>
              );
            })()}

            {/* 7. Price */}
            <Section id="price" title="Price">
              <div className="price-inputs">
                <div className="price-input-group">
                  <label htmlFor="min-price">Min</label>
                  <input
                    id="min-price"
                    type="number"
                    placeholder="$0"
                    value={filters.minPrice}
                    onChange={(e) => handleFilterChange('minPrice', e.target.value)}
                    min="0"
                    step="0.01"
                  />
                </div>
                <span className="price-separator">–</span>
                <div className="price-input-group">
                  <label htmlFor="max-price">Max</label>
                  <input
                    id="max-price"
                    type="number"
                    placeholder={`$${filterOptions.priceRange?.max || '100'}`}
                    value={filters.maxPrice}
                    onChange={(e) => handleFilterChange('maxPrice', e.target.value)}
                    min="0"
                    step="0.01"
                  />
                </div>
              </div>
            </Section>

            {/* 8. Availability */}
            <Section id="availability" title="Availability">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={!filters.outOfStockOnly && filters.inStockOnly !== false}
                  onChange={(e) => {
                    handleFilterChange('inStockOnly', e.target.checked);
                    if (e.target.checked) handleFilterChange('outOfStockOnly', false);
                  }}
                />
                <span>In stock only</span>
              </label>
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={!!filters.outOfStockOnly}
                  onChange={(e) => {
                    handleFilterChange('outOfStockOnly', e.target.checked);
                    if (e.target.checked) handleFilterChange('inStockOnly', false);
                    else handleFilterChange('inStockOnly', true);
                  }}
                />
                <span>Show out-of-stock only</span>
              </label>
            </Section>

            {/* 9. Other tags — Promo, Vintage, Signed, Error/Misprint */}
            {tagBuckets.others.length > 0 && (
              <Section id="other" title="Other" count={tagBuckets.others.length}>
                <div className="checkbox-group">
                  {tagBuckets.others.map(tagCheckbox)}
                </div>
              </Section>
            )}
          </div>

          {/* Sticky apply CTA — visible on mobile drawer only */}
          <div className="filter-drawer-foot">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={clearAllFilters}
              disabled={!hasActiveFilters}
            >
              Clear
            </button>
            <button
              type="button"
              className="btn btn-primary filter-drawer-apply"
              onClick={() => setShowFilters(false)}
            >
              Show {totalCount.toLocaleString()} {totalCount === 1 ? 'result' : 'results'}
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="loading">Loading products...</div>
      ) : products.length > 0 ? (
        <>
          <div className={view === 'list' ? 'product-list' : 'grid'}>
            {products.map(product => (
              <ProductCard key={product.id} product={product} variant={view} />
            ))}
          </div>
          {totalPages > 1 && (
            <div className="pagination">
              <button
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="btn btn-secondary"
              >
                Previous
              </button>
              <span className="page-info">Page {currentPage} of {totalPages}</span>
              <button
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                className="btn btn-secondary"
              >
                Next
              </button>
            </div>
          )}
        </>
      ) : (
        <div className="no-products">
          <p>No products found matching your criteria.</p>
          {hasActiveFilters && (
            <button type="button" className="btn btn-secondary" onClick={clearAllFilters}>
              Clear filters to see all products
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default Shop;
